import React, { useCallback } from 'react';
import { Spinner, Stack, Table } from 'react-bootstrap';
import SortingArrow from 'components/SortingArrow';
import { SortDirection } from 'constants/sorting';
import { WindxPainelOption, ModelsSortField } from 'types/Windx/Models/windxPainelOption';
import ModelsRow from '../ModelsRow';

export interface ModelsTableProps {
  models: WindxPainelOption[] | undefined;
  isFetching: boolean;
  pendingId: number | null;
  sortBy: ModelsSortField;
  sortDirection: SortDirection | undefined;
  setSortBy: (field: ModelsSortField) => void;
  setSortDirection: (dir: SortDirection | undefined) => void;
  onEdit: (model: WindxPainelOption) => void;
  onToggleAtivo: (model: WindxPainelOption) => void;
  onDelete: (model: WindxPainelOption) => void;
}

function useFieldSorter(
  field: ModelsSortField,
  setSortBy: (f: ModelsSortField) => void,
  setSortDirection: (d: SortDirection | undefined) => void,
) {
  return useCallback((direction?: SortDirection) => {
    setSortBy(field);
    setSortDirection(direction);
  }, [field, setSortBy, setSortDirection]);
}

// `field: null` => coluna não ordenável (ex.: Telas é array).
const COLUMNS: { title: string; field: ModelsSortField | null }[] = [
  { title: 'ID', field: 'id' },
  { title: 'Código', field: 'codigo' },
  { title: 'Label', field: 'label' },
  { title: 'Telas', field: null },
  { title: 'Categoria', field: 'categoria' },
  { title: 'Ordem', field: 'order_by' },
  { title: 'Status', field: 'ativo' },
];

export default function ModelsTable({
  models, isFetching, pendingId, sortBy, sortDirection, setSortBy, setSortDirection,
  onEdit, onToggleAtivo, onDelete,
}: ModelsTableProps) {
  const sortId = useFieldSorter('id', setSortBy, setSortDirection);
  const sortCodigo = useFieldSorter('codigo', setSortBy, setSortDirection);
  const sortLabel = useFieldSorter('label', setSortBy, setSortDirection);
  const sortCategoria = useFieldSorter('categoria', setSortBy, setSortDirection);
  const sortOrder = useFieldSorter('order_by', setSortBy, setSortDirection);
  const sortAtivo = useFieldSorter('ativo', setSortBy, setSortDirection);

  const sorters: Record<ModelsSortField, (d?: SortDirection) => void> = {
    id: sortId,
    codigo: sortCodigo,
    label: sortLabel,
    categoria: sortCategoria,
    order_by: sortOrder,
    ativo: sortAtivo,
  };

  return (
    <Table responsive hover data-testid="windx-modelos-tabela">
      <thead>
        <tr>
          {COLUMNS.map((col) => (
            <th key={col.title}>
              {col.field ? (
                <Stack direction="horizontal" gap={1}>
                  {col.title}
                  <SortingArrow
                    field={col.field}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    sort={sorters[col.field]}
                  />
                </Stack>
              ) : col.title}
            </th>
          ))}
          <th>Ações</th>
        </tr>
      </thead>
      <tbody>
        {isFetching && (
          <tr>
            <td colSpan={8} className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </td>
          </tr>
        )}
        {!isFetching && (models ?? []).length === 0 && (
          <tr>
            <td colSpan={8} className="text-center text-muted py-3">
              Nenhum modelo cadastrado.
            </td>
          </tr>
        )}
        {!isFetching && (models ?? []).map((m) => (
          <ModelsRow
            key={m.id}
            model={m}
            isPending={pendingId === m.id}
            onEdit={onEdit}
            onToggleAtivo={onToggleAtivo}
            onDelete={onDelete}
          />
        ))}
      </tbody>
    </Table>
  );
}
